import { PopupContainerProvider } from '@/components/ReportForm/Container/PopupContainer';
import { usePayeeDetailDrawer } from '@/features/ap_payees/components/PayeeDetailDrawer/context/PayeeDetailDrawerProvider';
import { useScrollToSectionOnEdit } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/useScrollToSectionOnEdit';
import {
  AnchorItemKey,
  PICAndContractDetailData,
  SECTION_KEYS,
} from '@/features/ap_payees/components/PayeeDetailDrawer/types';
import { useTranslation } from '@/i18n';
import { ButtonV2 } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, useCallback, useMemo } from 'react';
import styles from '../../styles.module.scss';
import { HeaderSection, SingleRow } from '../Common';
import { Edit } from './Edit';

const cx = classnames.bind(styles);

type PICAndContactInformationProps = {
  data?: PICAndContractDetailData | undefined;
  isEditable?: boolean;
};

export const PICAndContactInformation: FC<PICAndContactInformationProps> = ({
  data,
  isEditable = false,
}) => {
  const { t } = useTranslation();
  const { isEditingSection, setIsEditingSection } = usePayeeDetailDrawer();
  const isEditing = isEditingSection === SECTION_KEYS.PIC;
  const sectionRef = useScrollToSectionOnEdit(isEditing, SECTION_KEYS.PIC);

  const handleData = useMemo(() => {
    const {
      contact_office_members,
      contact_person_name,
      dept_name,
      contact_person_email,
      contact_person_phone_number,
      contact_person_full_address,
    } = data ?? {};

    return [
      {
        label: t('ap_payee_contact_office_member_names'),
        value:
          contact_office_members
            ?.map((member) => member.display_name)
            .join('、') ?? '',
      },
      {
        label: t('ap_payee_contact_person_name'),
        value: contact_person_name ?? '',
      },
      {
        label: t('ap_payee_contact_person_department'),
        value: dept_name ?? '',
      },
      {
        label: t('ap_payee_contact_person_email'),
        value: contact_person_email ?? '',
      },
      {
        label: t('ap_payee_contact_person_phone_number'),
        value: contact_person_phone_number ?? '',
      },
      {
        label: t('ap_payee_contact_person_full_address'),
        value: contact_person_full_address ?? '',
      },
    ];
  }, [data, t]);

  const onActionButtonClick = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      setIsEditingSection(SECTION_KEYS.PIC);
    },
    [setIsEditingSection]
  );

  if (isEditing) {
    return (
      <PopupContainerProvider>
        <div
          ref={sectionRef}
          id={AnchorItemKey.PIC_AND_CONTACT_INFORMATION}
          className={cx('sectionContainer')}
        >
          <Edit data={data} />
        </div>
      </PopupContainerProvider>
    );
  }

  return (
    <div
      ref={sectionRef}
      id={AnchorItemKey.PIC_AND_CONTACT_INFORMATION}
      className={cx('sectionContainer')}
    >
      <HeaderSection
        displayName={t('ap_payee_detail_drawer_pic_and_contact_information')}
        actionButton={
          isEditable && (
            <ButtonV2
              onClick={onActionButtonClick}
              isSecondary
              isDisabled={isEditingSection !== SECTION_KEYS.NULL}
            >
              {t('edit')}
            </ButtonV2>
          )
        }
      />
      <div className={cx('bodySectionContainer')}>
        {handleData.map((item, index) => (
          <SingleRow
            key={`${item.label}-${index}`}
            label={item.label}
            value={item.value ?? ''}
          />
        ))}
      </div>
    </div>
  );
};

PICAndContactInformation.displayName = 'PICAndContactInformation';
